import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Flex from '@atlaskit/primitives/components/flex';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';

import { DocumentSpaceTitle } from '@confluence/document-title/entry-points/DocumentSpaceTitle.ts';
import { EditContentButton } from '@confluence/edit-button/entry-points/EditContentButton.ts';
import { expVal } from '@confluence/feature-experiments';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { UseQueryInteropSupportedResult } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type {
	SpaceOverviewQuery$data,
	SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';
import { HubStatusLozenge } from '@confluence/company-hub-utils/entry-points/HubStatusLozenge.ts';
import { HubSettingsEntryButton } from '@confluence/company-hub-utils/entry-points/HubSettingsEntryButton.ts';
import { HubSettingsEntryButtonAppearance } from '@confluence/company-hub-utils/entry-points/HubSettingsEntryButtonAppearance.ts';

const editButtonStyles = xcss({
	position: 'relative',
	'::after': {
		content: '""',
		position: 'absolute',
		borderWidth: 'border.width',
		borderStyle: 'solid',
		borderRadius: 'radius.small',
		borderColor: 'color.border',
		inset: 'space.0',
		pointerEvents: 'none',
	},
});

// HubPreviewHeader should not overlay the content that follows it when the scroll position is at the top.
// Pulled here out of HubPreviewHeader to avoid vertical content shift given HubPreviewHeader loads lazily.
const fixedPreviewPlaceholderStyles = xcss({
	background: token('color.background.accent.gray.subtlest'), // Match BaseHubPreviewHeader
	height: `59px`, // BREADCRUMB_HEADER_HEIGHT like FixedSpaceHeaderContainer used by SpaceOverviewGroupedButtons, but lint doesn't allow using an import or a variable
});

const fixedPlaceholderStyles = xcss({
	height: '48px',
	containerType: 'inline-size',
});

// CompanyHubGroupedButtons should stick to the top of the content area
const headerContainerStyles = xcss({
	justifyContent: 'space-between',
	alignItems: 'center',
	width: '100cqw',
	height: '48px',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	// @ts-ignore because none of the allowed contants fit the use case
	zIndex: 12, // Like FixedSpaceHeaderContainer used by SpaceOverviewGroupedButtons
	position: 'fixed',
	backgroundColor: 'elevation.surface',
});

export type CompanyHubGroupedButtonsProps = {
	contentId: string;
	spaceKey: string;
	spaceOverviewQueryResult: UseQueryInteropSupportedResult<
		SpaceOverviewQuery$data,
		SpaceOverviewQuery$variables
	>;
	versionOverride?: number | null;
};

export const CompanyHubGroupedButtons = ({
	contentId,
	spaceKey,
	spaceOverviewQueryResult,
	versionOverride,
}: CompanyHubGroupedButtonsProps): React.JSX.Element | null => {
	const canEdit = getCanEdit(spaceOverviewQueryResult);
	const { canManageSettingsAutomation, canManageSomeUsers } = useSpaceManagementOperations({
		spaceKey,
		skip: !spaceKey || !expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false),
	});
	const canShowSettings =
		canEdit ||
		((canManageSettingsAutomation || canManageSomeUsers) &&
			expVal('permissions_rbac_dependencies_callsites', 'isEnabled', false));

	if (versionOverride != null) {
		return (
			<Box xcss={fixedPreviewPlaceholderStyles}>
				<HubPreviewHeader
					canEdit={canEdit}
					contentId={contentId}
					spaceKey={spaceKey}
					versionOverride={versionOverride}
				/>
			</Box>
		);
	}

	if (canShowSettings) {
		return (
			<Box xcss={[fixedPlaceholderStyles]}>
				<Flex xcss={[headerContainerStyles]}>
					<HubStatusLozenge />
					<Inline space="space.100">
						{canEdit && (
							<Inline xcss={[editButtonStyles]}>
								<EditContentButton contentId={contentId} isCompanyHubButton />
							</Inline>
						)}
						<HubSettingsEntryButton appearance={HubSettingsEntryButtonAppearance.ICON_ONLY} />
					</Inline>
				</Flex>
				<DocumentSpaceTitle spaceKey={spaceKey} />
			</Box>
		);
	}

	return null;
};

const getCanEdit = (
	spaceOverviewQueryResult: CompanyHubGroupedButtonsProps['spaceOverviewQueryResult'],
) =>
	Boolean(
		spaceOverviewQueryResult?.data?.space?.homepage?.operations?.some(
			(o) => o?.operation === 'update',
		),
	);

const HubPreviewHeader = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-HubPreviewHeader" */ './HubPreviewHeader.tsx'))
			.HubPreviewHeader,
});
